<template>

    <spinner-until :condition="!showSpinner">

        <div class="survey-form" v-if="!showSpinner">

            <h2 class="survey-form__title">{{ $i18n.gt('HEADING_QUESTIONNAIRE_TITLE') }}</h2>

            <div class="survey-form__header">

                <!-- section titles -->
                <div class="section-title-wrapper mb-2">
                    <b-badge class="section-title"
                             v-for="section in survey.questionnaire.sections" :key="section.name"
                             :variant="getSectionBadgeVariant(section)"
                             @click="goToSection(section)">
                        {{ $i18n.gt(`QUESTIONNAIRE_${survey.questionnaire.code}_SECTION_${section.name}_TITLE`) }}
                    </b-badge>
                </div>

                <!-- progress bar with question links -->

                <div class="question-index">
                    <b-col md="12" xl="12">
                        <div class="question-link"
                             v-for="q in getApplicableQuestions()" :key="q.name"
                             @click="goToQuestion(q)"
                             :class="{ completed: isCompleted(q),
                                       current: q.name == question.name }"
                             :style="getQuestionIndexWidth()">
                            <span class="current-question-indicator"
                                  v-if="q.name == question.name">
                                <shs-icon name="chevron-top"></shs-icon>
                            </span>
                        </div>
                    </b-col>
                </div>
            </div>

            <!-- DietID iframe (see SurveyFormDietidMixin) -->

            <div v-if="question.type == 'DIETID'">
                <vue-friendly-iframe ref="dietidIframe"
                                     v-if="dietidIframeUrl"
                                     :src="dietidIframeUrl"
                                     class-name="dietid-iframe"
                                     @iframe-load="onDietidIframeLoad"
                                     @load="onDietidLoad">
                </vue-friendly-iframe>
            </div>

            <div v-else class="survey-form__body" v-cloak>

                <!-- nav buttons -->

                <div class="question-form__actions row mb-3">
                    <div class="question-form__buttons-save col-4">
                        <b-button :variant="'save'"
                                  v-on:click="saveAndExit">
                            {{ $i18n.gt('LABEL_QUESTIONNAIRE_SAVE_AND_EXIT') }}
                            <shs-icon name="external-link" class="ml-2"></shs-icon>
                        </b-button>
                    </div>
                    <div class="question-form__nav-buttons col-8">
                        <div class="question-form__button-back">
                            <b-button :variant="'primary'"
                                      :disabled="disablePrevious" x
                                      v-on:click="previousQuestion">
                                {{ $i18n.gt('LABEL_QUESTIONNAIRE_GO_BACK') }}
                            </b-button>
                        </div>
                        <div class="question-form__button-next">
                            <b-button :variant="'primary'"
                                      :disabled="disableNext"
                                      v-on:click="nextQuestion">
                                {{ $i18n.gt('LABEL_QUESTIONNAIRE_CONTINUE') }}
                            </b-button>
                        </div>
                    </div>
                </div>

                <div class="question-form">
                    <!-- question body -->

                    <div class="container" v-if="question && question.type">

                        <div v-if="question.type == 'INSTRUCTIONS'">
                            <div class="question-form__question col-12">
                                <div class="question-form__question-text">
                                    <div v-html="$i18n.translateQuestionField(question, 'PREAMBLE')"
                                         v-if="$i18n.translateQuestionField(question, 'PREAMBLE')"></div>
                                    <div v-html="$i18n.translateQuestionField(question, 'QUESTION_TEXT')"></div>
                                </div>
                                <div class="question-form__question-hint"
                                     v-if="$i18n.translateQuestionField(question, 'TIP')">
                                    <b-alert show
                                             v-html="$i18n.translateQuestionField(question, 'TIP')">
                                    </b-alert>
                                </div>
                            </div>
                        </div>

                        <div class="row" v-else>
                            <div v-if="question.static_question_name" class="question-form__question col-md-6">
                                <div class="question-form__question-text">
                                    <div v-html="$i18n.translateQuestionField(question, 'PREAMBLE')"
                                         v-if="$i18n.translateQuestionField(question, 'PREAMBLE')"></div>
                                </div>
                                <div class="question-form__question-hint">
                                    <b-alert show
                                             v-html="$i18n.gt(`QUESTION_${question.static_question_name}_QUESTION_TEXT`)"/>
                                </div>
                                <div class="question-form__question-hint"
                                     v-if="$i18n.translateQuestionField(question, 'TIP')">
                                    <b-alert show
                                             v-html="$i18n.translateQuestionField(question, 'TIP')">
                                    </b-alert>
                                </div>
                            </div>
                            <div v-else class="question-form__question col-md-6">
                                <div class="question-form__question-text">
                                    <div v-html="$i18n.translateQuestionField(question, 'PREAMBLE')"
                                         v-if="$i18n.translateQuestionField(question, 'PREAMBLE')"></div>
                                    <div v-html="$i18n.translateQuestionField(question, 'QUESTION_TEXT')"></div>
                                </div>
                                <div class="question-form__question-hint"
                                     v-if="$i18n.translateQuestionField(question, 'TIP')">
                                    <b-alert show
                                             v-html="$i18n.translateQuestionField(question, 'TIP')">
                                    </b-alert>
                                </div>
                                <div v-if="$mq === 'sm' || $mq === 'md'">
                                    <br>
                                    <b-alert show>
                                        {{ $i18n.gt('QUESTION_RTC_MOBILE_SCROLL') }}
                                    </b-alert>
                                </div>
                            </div>

                            <div class="question-form__answer col-md-6 question-inputs"
                                 v-if="question.options.length">

                                <!-- validation message -->

                                <div class="col-12 alert alert-warning"
                                     v-if="validation.message"
                                     v-html="validation.message">
                                </div>

                                <!-- maybe-prefilled message -->

                                <div class="col-12 alert alert-warning"
                                     v-if="question.maybe_prefilled">
                                    {{ $i18n.gt('MESSAGE_QUESTION_MAYBE_PREFILLED') }}
                                </div>

                                <div v-if="question.type == 'SELECT_ANY'"
                                     class="btn-group-toggle btn-group-vertical">
                                    <label :class="{active: optionValuesList[index] != ''}"
                                           v-for="(option,index) in question.options" :key="index">
                                        <b-form-checkbox v-model="optionValuesList[index]"
                                                         value="on"
                                                         unchecked-value=""
                                                         @change="none = false"
                                                         @input="autosave()">
                                            {{ $i18n.translateQuestionField(question, `OPTIONS_${index}`) }}
                                        </b-form-checkbox>
                                    </label>
                                    <label class="mt-3"
                                           :class="{active: question.response.status == 'NONE'}">
                                        <b-form-checkbox v-model="none"
                                                         @input="autosave()"
                                                         class="question-form__none-check"
                                                         value="true">
                                            {{ $i18n.gt('LABEL_QUESTIONNAIRE_NONE_OF_ABOVE') }}
                                        </b-form-checkbox>
                                    </label>
                                </div>

                                <div v-if="question.type == 'SELECT_ONE'">
                                    <b-form-radio-group v-model="optionValuesList[0]"
                                                        @input="autosave()"
                                                        class="col-12"
                                                        stacked>
                                        <b-form-radio v-for="(opt, i) in getRadioOptions(question)" :key="i"
                                                      :value="opt.value"
                                                      :class="{active: optionValuesList[0] == opt.value}">
                                            {{ opt.text }}
                                        </b-form-radio>
                                    </b-form-radio-group>
                                </div>
                                <div v-if="question.type == 'SCALE'">
                                    <b-form-radio-group v-model="optionValuesList[0]"
                                                        @input="autosave()"
                                                        class="col-12"
                                                        buttons>
                                        <b-form-radio class="col px-0"
                                                      v-for="(opt, i) in getRadioOptions(question)" :key="i"
                                                      :value="opt.value"
                                                      :class="{active: optionValuesList[0] == opt.value}">
                                            {{ i + 1 }}
                                        </b-form-radio>
                                    </b-form-radio-group>
                                    <div class="row pl-5 pr-5 pt-1 justify-content-between">
                                        <div v-for="(scale, j) in question['scale']" :key="j">
                                            {{ $i18n.gt(`QUESTIONNAIRE_${scale}`) }}
                                        </div>
                                    </div>
                                </div>

                                <div v-if="question.type == 'MULTI_SELECT_ONE'">
                                    <div v-for="(group, j) in question.multi_options" :key="j"
                                         class="mb-4">
                                        {{ $i18n.gt(`QUESTION_${question.option_groups[j]}_PROMPT`) }}
                                        <!-- if options are letters, use regular radios not button radios, pull option text from translations -->
                                        <b-form-radio-group v-if="question.multi_options[j][0] === 'A'"
                                                            v-model="optionValuesList[j]"
                                                            @input="autosave()"
                                                            class="col-12">
                                            <b-form-radio v-for="(opt, i) in getMultiRadioOptions(question, j)" :key="i"
                                                          :value="opt.value"
                                                          :class="{active: optionValuesList[j] == opt.value}">
                                                {{ opt.text }}
                                            </b-form-radio>
                                        </b-form-radio-group>
                                        <b-row v-else>
                                            <b-form-radio-group v-model="optionValuesList[j]"
                                                                @input="autosave()"
                                                                class="col-12"
                                                                buttons>
                                                <b-form-radio class="col mr-2 px-0 na-btn"
                                                              v-for="(opt, i) in getMultiRadioOptions(question, j, 0, 0)"
                                                              :key="i"
                                                              :value="opt.value"
                                                              :class="{active: optionValuesList[j] == opt.value}">
                                                    {{ question.multi_options[j][i] }}
                                                </b-form-radio>

                                                <b-form-radio class="col px-0"
                                                              v-for="(opt, i) in getMultiRadioOptions(question, j, 1, 1)"
                                                              :key="i"
                                                              :value="opt.value"
                                                              :class="{active: optionValuesList[j] == opt.value}"
                                                              style="border-top-left-radius: 3px; border-bottom-left-radius: 3px">
                                                    {{
                                                        question.multi_options[j][0] === 'N/A' ? question.multi_options[j][i + 1] : question.multi_options[j][i]
                                                    }}
                                                </b-form-radio>

                                                <b-form-radio :value="opt.value"
                                                              v-for="(opt, i) in getMultiRadioOptions(question, j, 2, 10)"
                                                              :key="i"
                                                              class="col px-0"
                                                              :class="{active: optionValuesList[j] == opt.value}">
                                                    {{
                                                        question.multi_options[j][0] === 'N/A' ? question.multi_options[j][i + 2] : question.multi_options[j][i + 1]
                                                    }}
                                                </b-form-radio>
                                            </b-form-radio-group>
                                        </b-row>

                                        <div class="row pl-5 pr-5 pt-1 justify-content-between">
                                            <div>{{
                                                    $i18n.gt(`QUESTION_${question.option_groups[j]}_SCALE_LEFT`)
                                                }}
                                            </div>
                                            <div>{{
                                                    $i18n.gt(`QUESTION_${question.option_groups[j]}_SCALE_RIGHT`)
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="question.type == 'ENTER_DATA'">
                                    <div v-for="(opt, index) in getEnterDataOptions(question)"
                                         :key="index">
                                        <b-input-group>
                                            <b-input-group-text slot="prepend"
                                                                v-if="opt.prefix">
                                                {{ opt.prefix }}
                                            </b-input-group-text>
                                            <!-- TODO: allow_decimal, allow_negative -->
                                            <b-form-input type="number" size="3" min=""
                                                          :ref="index == 0 ? 'input0' : ''"
                                                          v-model="optionValuesList[index]"
                                                          @input="autosave()">
                                            </b-form-input>
                                            <b-input-group-text slot="append"
                                                                v-if="opt.suffix">
                                                {{ opt.suffix }}
                                            </b-input-group-text>
                                        </b-input-group>
                                        <br/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="question-form__actions row mb-3" v-if="$mq === 'sm' || $mq === 'md'">
                <div class="question-form__buttons-save col-4">
                    <b-button :variant="'save'"
                              v-on:click="saveAndExit">
                        {{ $i18n.gt('LABEL_QUESTIONNAIRE_SAVE_AND_EXIT') }}
                        <shs-icon name="external-link" class="ml-2"></shs-icon>
                    </b-button>
                </div>
                <div class="question-form__nav-buttons col-8">
                    <div class="question-form__button-back">
                        <b-button :variant="'primary'"
                                  :disabled="disablePrevious" x
                                  v-on:click="previousQuestion">
                            {{ $i18n.gt('LABEL_QUESTIONNAIRE_GO_BACK') }}
                        </b-button>
                    </div>
                    <div class="question-form__button-next">
                        <b-button :variant="'primary'"
                                  :disabled="disableNext"
                                  v-on:click="nextQuestion">
                            {{ $i18n.gt('LABEL_QUESTIONNAIRE_CONTINUE') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- debug -->
            <div class="debug" v-if="false">
                <hr/>
                <!--<span class="badge">Index: {{ questionIndex }}</span>-->
                <span class="badge">Question ID: {{ question.id }}</span>
                <span class="badge">{{ question.name }}</span>
                <span class="badge">Applicable: {{
                        isApplicableQuestion(question)
                    }}</span>
                <span class="badge">Answerable: {{
                        isAnswerableQuestion(question)
                    }}</span>
                <span class="badge">showif: {{ question.showif }}</span>
                <span class="badge">Validation Type: {{
                        question.validation
                    }}</span>
                <span class="badge">Validation: {{ validation }}</span>
                <br/>
                <span class="badge">Response ID: {{
                        question.response ?
                            question.response.id : ''
                    }}</span>
                <span class="badge">optionValuesList: {{
                        optionValuesList
                    }}</span>
            </div>

        </div>
    </spinner-until>

</template>

<!-- -------------------------------------------------------- -->

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import SurveyFormNavigationMixin from '../providers/SurveyFormNavigationMixin'
import SurveyFormDietidMixin from '../providers/SurveyFormDietidMixin'

/**
 * Form for a Participant to complete a Survey, with navigation controls
 */
export default Vue.extend({
    mixins: [SurveyFormNavigationMixin, SurveyFormDietidMixin],
    props: {
        surveyId: Number,
        onExit: Function,
        forConifer: {type: Boolean, default: false}
    },
    data() {
        return {
            survey: {},
            showSpinner: true,
            validation: {
                warned: false,
                valid: true,
                message: null
            }
        }
    },
    computed: {
        question() {
            if (this.survey.current_question) {
                return this.questions[this.survey.current_question]
            } else {
                return (this.questions ? this.questions[0] : {})
            }
        },
        optionValuesList() {
            return this.question.response ? this.question.response.optionValuesList : []
        },
        none: {
            get() {
                return this.question.response && this.question.response.status == 'NONE'
            },
            set(val) {
                if (val) {
                    this.clearOptionValues()
                    this.question.response.status = 'NONE'
                } else { // box was unchecked
                    this.question.response.status = null
                }
            }
        },

        sections() {
            return this.survey.questionnaire.sections
        },
        questions() {
            return this.survey.questionnaire.questions
        },
        section() {
            return _.find(this.sections, (s) => {
                return this.question && s.question_names.indexOf(this.question.name) != -1
            })
        },
    },
    methods: {
        loadSurvey() {
            this.$store.dispatch('getSurvey', {surveyId: this.surveyId})
                .then((survey) => {
                    this.survey = this.$store.state.survey
                    this.$api.log(this, 'survey', this.survey, 'fill', `Started/resumed survey #${this.survey.id}`)
                    this.showSpinner = false
                })
        },

        clearOptionValues() {
            this.question.response.optionValuesList = _.map(this.question.options, (opt) => '')
        },
        validateQuestion() {
            this.validation.valid = true // innocent until proven guilty
            if (this.question.type == 'INSTRUCTIONS' || this.question.type == 'DIETID' || this.question.validation == 'NONE') {
                return
            }

            var minMaxOk = true
            if (this.question.type == 'ENTER_DATA') {
                minMaxOk = _.every(this.optionValuesList, (ov, index) => {
                    const min = _.toNumber(this.question.min_values[index])
                    const max = _.toNumber(this.question.max_values[index])
                    if ((min !== '' && ov !== '' && _.toNumber(ov) < min) ||
                        (min !== '' && ov !== '' && _.toNumber(ov) > max)) {
                        return false
                    } else {
                        return true
                    }
                })
            }

            if (!this.isCompleted(this.question)) {
                this.validation.valid = false
                this.validation.message = this.$i18n.translateQuestionField(this.question, 'VALIDATION_MESSAGE')
                    || this.$i18n.gt('MESSAGE_ENTER_VALUE')

            } else if (!minMaxOk) {
                // for minmax violations, if a warning has already been displayed, we can go ahead if !minmax_enforced
                if (this.validation.warned && !this.question.enforce_minmax) {
                    this.validation.valid = true
                } else {
                    this.validation.valid = false
                }
                this.validation.warned = true
                this.validation.message = this.$i18n.translateQuestionField(this.question, 'MINMAX_MESSAGE')
            } else {
                this.validation.warned = false
                this.validation.message = null
            }
        },
        isCompleted(question) {

            if (question.type == 'INSTRUCTIONS') {
                return true
            } else if (question.type == 'DIETID') {
                return this.isCompletedDietidQuestion(question) // see SurveyFormDietidMixin
            } else if (question.type == 'SELECT_ANY') {
                // allow user to select no options
                return true
            }

            if (!question.response || !question.response.optionValuesList) {
                return false
            }

            if (question.response.status == 'NONE') {
                return true
            }

            var opts = question.response.optionValuesList

            // NB: response.optionValuesList may have a length less than the number of question options
            var aBlank = isBlank(opts[0])
            var bBlank = opts.length <= 1 || (opts.length > 1 && isBlank(opts[1]))
            var cBlank = opts.length <= 2 || (opts.length > 2 && isBlank(opts[2]))

            var bothBlank = aBlank && bBlank
            var neitherBlank = !aBlank && !bBlank

            var allBlank = aBlank && bBlank && cBlank
            var noneBlank = !aBlank && !bBlank && !cBlank

            var abBlank = aBlank && bBlank && !cBlank
            var acBlank = aBlank && !bBlank && cBlank
            var bcBlank = !aBlank && bBlank && cBlank

            var aOnlyBlank = aBlank && !bBlank && !cBlank
            var bOnlyBlank = !aBlank && bBlank && !cBlank
            var cOnlyBlank = !aBlank && !bBlank && cBlank

            if (question.validation == 'A_OR_B') {
                return !(bothBlank || neitherBlank)
            } else if (question.validation == 'A_OR_B_AND_C') {
                return !(allBlank || noneBlank || abBlank || acBlank || bcBlank || cOnlyBlank)
            } else if (question.validation == 'ALL') {
                if (question.type == 'MULTI_SELECT_ONE') {
                    if (opts.length == 3) {
                        const emptyOpt = opts.findIndex(isBlank)
                        return emptyOpt == -1
                    } else return false
                } else {
                    const emptyOpt = _.find(opts, (opt) => {
                        return isBlank(opt)
                    })
                    return emptyOpt == null
                }
            } else {
                // default: at least one non-empty field
                const nonEmptyOpt = _.find(opts, (opt) => {
                    return !isBlank(opt)
                })
                return nonEmptyOpt != null
            }
        },

        doAutosave() {
            return this.$api.updateItem(this, 'survey', this.survey, false)
        },
        saveAndExit(e) {
            this.doAutosave()
                .then(() => {
                    this.showThankYou()
                        .then(() => {
                            if (this.forConifer) {
                                this.$emit('exit')
                            } else {
                                this.showSpinner = true
                                // reload participant and surveys
                                this.$store.dispatch('getPatientParticipant', {
                                    participantId: this.survey.participant_id,
                                    force: true
                                })

                                if (this.$user.practitionerId()) {
                                    this.$router.push(`/participant/chart/id/${this.survey.participant_id}`)
                                } else {
                                    this.$router.push('/questionnaires')
                                }
                                if (this.onExit) {
                                    this.onExit()
                                }
                            }
                        })
                })
        },
        showThankYou() {
            const msg = this.survey.questionnaire.is_practitioner_administered
                ? 'MESSAGE_QUESTIONNAIRE_COMPLETED_PRACTITIONER'
                : 'MESSAGE_QUESTIONNAIRE_THANK_YOU'
            return this.$bvModal.msgBoxOk(this.$i18n.gt(msg))
        }
    },
    created() {
        this.loadSurvey()
        this.autosave = _.debounce(this.doAutosave, 1000)
    },
})

function isBlank(val) {
    return val == null || val == undefined || val.replace(/ /g, '') == ''
}

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">

@import '../../sass/variables_mixins';

.survey-form {
  max-width: 1140px;
  margin: 0 auto;
  padding-bottom: 2em;
}

.survey-form__header, .survey-form__footer {
  padding: 15px;
  background-color: #fff;
}

.survey-form__header {
  margin-bottom: 3em;
  border: solid 1px var(--border-color);
}

.survey-form__footer {
  margin-top: 2em;
}

.survey-form__body {
  //padding: 15px;
  //background: #eee;
  max-width: 1140px;
  margin: 0 auto;
}

.survey-form__footer {
  font-size: 13px;
  color: #888;
}

.survey-form__title {
  margin-bottom: .5em;
}

.section-title {
  text-transform: uppercase;
  font-weight: 300;
  cursor: pointer;

  &.badge-default {
    color: var(--base-color);
  }
}

.question-form {
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--base-color);
  border: solid 1px var(--border-color);


  > .container {
    max-width: 100% !important;
  }
}

.question-index > div {
  //border: solid 1px #ccc;
  background-color: #e2e2e2;
  position: relative;
  display: inline-block;
  text-align: left;
  vertical-align: top;
  width: 100%;
  height: 18px;
  line-height: 18px;
  padding: 0;
  margin: 5px 10px 10px 0;
  border-radius: 3px;
}

.question-link {
  position: relative;
  display: inline-block;
  margin: 0;
  height: 18px;
  line-height: 18px;
  cursor: pointer;
  @include transition(all .3s);

  &:hover {
    background-color: #aaa;
  }

  &.current, &.current:hover {
    //background-color: #555;
    cursor: default;
  }

  &.current.completed, &.current.completed:hover {
    background-color: var(--completed-color-darken-15);
  }

  &.completed {
    background-color: var(--completed-color);

    &:hover {
      background-color: var(--completed-color-darken-5);
    }
  }
}

.current-question-indicator {
  @include horizontal-center;
  top: 18px;

  > svg {
    margin: 0 !important;

    use {
      fill: #888;
    }
  }
}

.question-form__question-text {
  color: #fff;
  font-size: 1.25em;
}

.question-form__question-hint {
  margin: 20px 40px 20px 0;

  > div {
    //background-color: var(--base-color-lighten-10);
    border: none;
  }
}

.question-form__actions {
  margin-top: 1em;
  margin-left: auto;
  margin-right: auto;
  justify-content: space-between;

  .btn {

    @media (min-width: $tablet-portrait) {
      padding: .5rem 1.25rem;
    }
    @media (min-width: $tabletLandscape) {
      padding: .65rem 1.5rem;
      //border-radius: 1.5rem;
      // font-size: 16px;
    }
  }
}

.question-form__buttons-save {
  padding: 0;
}

.question-form__nav-buttons {
  display: flex;
  padding: 0;
  justify-content: flex-end;
  @media (max-width: $tablet-portrait - 1px) {
    //margin-top:1em;

  }
  @media (min-width: $tablet-portrait) {

  }
}

.question-form__button-back, .question-form__button-next {
  .btn {
    //@include button-blue;
  }
}

.question-form__button-back .btn {
  &:before {
    content: "<";
    display: inline-block;
    padding-right: 8px;
  }
}

.question-form__button-next .btn {
  margin-left: .4rem;

  &:after {
    content: ">";
    display: inline-block;
    padding-left: 8px;
  }

  @media (min-width: $tablet-portrait) {

  }
}

.question-form__buttons-save {
  .btn:nth-child(2) {
    //margin:0 1em;
  }
}

@media (max-width: 767px) {
  .question-form__question {
    padding: 2em;
  }
}

.question-form__answer {
  padding: 1em;
  background-color: #fff;
}

@media (min-width: 768px) {
  .question-form__question, .question-form__answer {
    padding: 2em;
  }
}

.dietid-iframe {
  width: 100%;
  height: 700px;
  padding: 0;
  background-color: transparent;
  border: solid 1px #fff !important;
}

.na-btn {
  background-color: hsl(197.7, 76.1%, calc(50.8% + 40%)) !important;
  color: #0c5460 !important;
  border-radius: 3px !important;

  &.active {
    background-color: #545b62 !important;
    border-color: #4e555b !important;
    color: #fff !important
  }
}

.debug {
  .badge {
    color: #666 !important;
  }
}
</style>
